/**
 * Placeholders the instance prompt resolver substitutes at message time.
 * Offered as `{{…}}` autocomplete + a categorised sidebar in the prompt
 * editor.
 *
 * This list is the single provisioning-side catalogue. It MUST stay in sync
 * with the instance resolver in
 * backend/src/employee/prompt-placeholder.service.ts (buildPlaceholderContext):
 * every `ctx.*` key there needs an entry here and vice-versa. A backend test
 * (prompt-placeholder-catalog-parity.spec.ts) fails the build on drift — an
 * advertised placeholder the resolver can't fill would otherwise ship raw
 * (`{{x}}`) to customers.
 *
 * The admin-ui does not depend on @nbts/shared, so this can't be a shared
 * import; the parity test is what binds the two lists.
 */

/** Buckets the sidebar groups placeholders under, in display order. */
export type PlaceholderCategory =
  | "Company"
  | "Employee"
  | "Email"
  | "Workflows"
  | "Products & payments"
  | "CRM";

export const PLACEHOLDER_CATEGORIES: readonly PlaceholderCategory[] = [
  "Company",
  "Employee",
  "Email",
  "Workflows",
  "Products & payments",
  "CRM",
];

export interface PromptPlaceholder {
  name: string;
  description: string;
  category: PlaceholderCategory;
}

export const PROMPT_PLACEHOLDERS: readonly PromptPlaceholder[] = [
  // ── Company ────────────────────────────────────────────────────────────
  { name: "company_name", description: "Company name", category: "Company" },
  {
    name: "company_contacts",
    description: "Company contacts block (address, phone, website, socials)",
    category: "Company",
  },
  {
    name: "company_contacts_address",
    description: "Company address",
    category: "Company",
  },
  // ── Employee ───────────────────────────────────────────────────────────
  {
    name: "employee_name",
    description: "Employee display name (for greetings)",
    category: "Employee",
  },
  {
    name: "employee_position",
    description: "Employee name / position",
    category: "Employee",
  },
  {
    name: "employee_mission",
    description: "Employee description / mission",
    category: "Employee",
  },
  {
    name: "employee_goal",
    description: "Employee value proposition / goal",
    category: "Employee",
  },
  {
    name: "employee_communication_tone",
    description: "Communication tone",
    category: "Employee",
  },
  {
    name: "employee_communication_languages",
    description: "Languages, comma-separated",
    category: "Employee",
  },
  {
    name: "employee_greeting",
    description: "Greeting",
    category: "Employee",
  },
  {
    name: "employee_response_length",
    description: "Response length (derived from tone)",
    category: "Employee",
  },
  {
    name: "employee_emoji_usage",
    description: "Emoji usage (derived from tone)",
    category: "Employee",
  },
  {
    name: "employee_empath_level",
    description: "Empathy level (derived from tone)",
    category: "Employee",
  },
  {
    name: "employee_other_rules",
    description: "Custom rules, formatted as a list",
    category: "Employee",
  },
  {
    name: "employee_custom_config",
    description: "Filled values of the operator-defined config blocks",
    category: "Employee",
  },
  // ── Email ──────────────────────────────────────────────────────────────
  {
    name: "email_escalation_to",
    description: "Escalation email recipient (email_responder)",
    category: "Email",
  },
  {
    name: "system_language",
    description: "The operator's UI language (for the translation field)",
    category: "Email",
  },
  // ── Workflows ──────────────────────────────────────────────────────────
  {
    name: "employee_workflow_price_consultation",
    description: "Price-consultation workflow steps",
    category: "Workflows",
  },
  {
    name: "employee_workflow_upsell",
    description: "Upsell workflow steps",
    category: "Workflows",
  },
  // ── Products & payments ────────────────────────────────────────────────
  {
    name: "employee_payment_methods_list",
    description: "Enabled payment methods (JSON)",
    category: "Products & payments",
  },
  {
    name: "product_categories_list",
    description: "Active product categories (JSON)",
    category: "Products & payments",
  },
  {
    name: "product_categories_guide",
    description:
      "Active product categories with their descriptions and categoryId — the index the bot uses to pick a category before loading its products",
    category: "Products & payments",
  },
  {
    name: "product_categories_list_with_description",
    description:
      "Active product categories as a plain readable block (name + full description, no categoryId) — the spoken fact bank for voice/outreach bots with no mid-call lookup tool",
    category: "Products & payments",
  },
  {
    name: "products_special_conditions",
    description: "Active product special conditions",
    category: "Products & payments",
  },
  {
    name: "pipeline_stages_list",
    description:
      "Deal pipelines with each stage's name and slug (JSON) — the slugs crm_deal_pipeline_stage_update accepts",
    category: "CRM",
  },
];

/** Placeholders grouped by category, in `PLACEHOLDER_CATEGORIES` order. */
export function groupPlaceholdersByCategory(): Array<{
  category: PlaceholderCategory;
  items: PromptPlaceholder[];
}> {
  return PLACEHOLDER_CATEGORIES.map((category) => ({
    category,
    items: PROMPT_PLACEHOLDERS.filter((p) => p.category === category),
  })).filter((group) => group.items.length > 0);
}
